import {produce} from 'immer';
import React, {useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Modal, Placeholder} from 'semantic-ui-react';

import type * as Generated from '../../generated/graphql';
import {useQuery} from '../../state/graphql/apolloWithAbort';
import {useSelector} from '../../state/hooks';
import {type ReportViewRef} from '../../state/reports/types';
import {
  type Stargazer,
  STARGAZERS_QUERY,
  type StargazersQueryData,
} from '../../state/views/graphql';
import * as urls from '../../util/urls';

const TRIGGER_LOAD_BREAKPOINT = 240;

interface StargazerModalInnerProps {
  reportViewRef: ReportViewRef;
}

const StargazerModalInner: React.FC<StargazerModalInnerProps> = props => {
  const viewServerID = useSelector(
    state => state.views.views[props.reportViewRef.id].id
  );

  const query = useQuery<
    StargazersQueryData,
    Generated.StargazersQueryQueryVariables
  >(STARGAZERS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {viewID: viewServerID!, limit: 12},
    skip: !viewServerID,
    notifyOnNetworkStatusChange: true,
  });

  const scrolledToBottom = (el: Element) =>
    el.scrollTop > el.scrollHeight - el.clientHeight - TRIGGER_LOAD_BREAKPOINT;

  const checkForLoadingMore = () => {
    const modalWrapper = document.querySelector('.ui.page.modals');
    if (
      modalWrapper &&
      scrolledToBottom(modalWrapper) &&
      !query.loading &&
      query.data?.view.stargazers.pageInfo.hasNextPage &&
      viewServerID
    ) {
      query.fetchMore({
        variables: {
          viewID: viewServerID,
          limit: 6,
          cursor: query.data?.view.stargazers.pageInfo.endCursor,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return produce(prev, draft => {
            draft.view.stargazers.pageInfo =
              fetchMoreResult.view.stargazers.pageInfo;
            draft.view.stargazers.edges.push(
              ...fetchMoreResult.view.stargazers.edges
            );
          });
        },
      });
    }
  };

  useEffect(() => {
    const interval = window.setInterval(checkForLoadingMore, 500);
    return () => {
      window.clearInterval(interval);
    };
  });

  let stargazers: Stargazer[] = [];
  if (!query.loading) {
    stargazers = query.data?.view.stargazers.edges.map(e => e.node) || [];
  }

  if (!query.loading && stargazers.length === 0) {
    return (
      <Modal.Content>
        <div className="modal-header">No stars yet.</div>
        <div className="no-stars-watermark">Be the first?</div>
      </Modal.Content>
    );
  }

  return (
    <Modal.Content>
      <div className="modal-header">Stars</div>
      {stargazers.map((user, index) => (
        <React.Fragment key={user.username}>
          <div className="mini-profile-card">
            <Link to={urls.profilePage(user.username)}>
              <img
                src={user.photoUrl}
                alt={`${user.username}'s profile picture`}
              />
            </Link>
            <div className="text-content">
              <Link className="username" to={urls.profilePage(user.username)}>
                {user.username}
              </Link>
              {user.userInfo && user.userInfo.company && (
                <div className="company">{user.userInfo.company}</div>
              )}
            </div>
          </div>
          {index !== stargazers.length - 1 && <div className="divider"></div>}
        </React.Fragment>
      ))}
      {(query.loading || query.data?.view.stargazers.pageInfo.hasNextPage) && (
        <Placeholder>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
        </Placeholder>
      )}
    </Modal.Content>
  );
};

interface StargazerModalProps {
  reportViewRef: ReportViewRef;
  open: boolean;
  onClose: () => void;
}

const StargazerModal: React.FC<StargazerModalProps> = props => {
  return (
    <Modal
      className="stargazer-modal"
      open={props.open}
      onClose={props.onClose}
      onClick={(e: any) => {
        // for some reason clicking on the modal
        // without this causes re-render
        e.stopPropagation();
      }}
      size="mini">
      <StargazerModalInner
        reportViewRef={props.reportViewRef}></StargazerModalInner>
    </Modal>
  );
};

export default StargazerModal;
